import React, { memo, useEffect, useMemo, useState } from 'react';

import { Button, Modal, Select } from '@components/common';
import NodeConfiguration from '@components/routes/AdminPanel/SiteProfile/LimenetProfile/NodeConfiguration';

import { useQueryCrowdCellDatesExist } from '@hooks/crowdCells/useQueryCrowdCellDatesExist';

import { generateSelectOptions } from '@utils/generateSelectOptions/generateSelectOptions';
import { prepareCellData } from './helpers/prepareCellData';

import { TCellsData } from '@interfaces/cells/cells';
import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { ETechnologiesValues, TTechnologiesValues } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';

import ParameterItem from './ParameterItem';

import styles from './SiteParameters.module.scss';

interface ISiteParametersProps {
	technologyValue: TTechnologiesValues;
	cellsData: TCellsData;
	site: ISiteProfile;
}

const NETWORK_PARAMETERS_LABEL = 'Network parameters';

const SiteParameters = ({
	technologyValue,
	cellsData,
	site,
}: ISiteParametersProps) => {
	const options = useMemo(() => generateSelectOptions({
		data: cellsData?.items,
		value: 'cellId',
		label: 'cellId',
	}), [cellsData?.items]);

	const [selectedCellId, setSelectedCellId] = useState<TSelectOption>();
	const [selectedCellData, setSelectedCellData] = useState<Record<string, any>>({});
	const [isShownConfiguration, setIsShownConfiguration] = useState<boolean>(false);
	const [technologyForConfig, setTechnologyForConfig] = useState<TTechnologiesValues | undefined>();

	useEffect(() => {
		switch (technologyValue) {
			case String(ETechnologiesValues.T_4G_TDD):
				setTechnologyForConfig(ETechnologiesValues.C_4G_TDD);
				break;
			case String(ETechnologiesValues.T_4G_FDD):
				setTechnologyForConfig(ETechnologiesValues.C_4G_FDD);
				break;
			case String(ETechnologiesValues.T_5G):
				setTechnologyForConfig(ETechnologiesValues.C_5G);
				break;
			default:
				setTechnologyForConfig(undefined);
		}
	}, [technologyValue]);

	const { crowdCellConfigDateExists, isInitialLoadingCrowdCellConfigDateExists } = useQueryCrowdCellDatesExist({
		crowdCellId: site.crowdCell?.id,
		type: technologyForConfig,
	});

	const closeConfigurationModal = () => setIsShownConfiguration(false);

	const handleChangeCellId = (value: unknown) => {
		setSelectedCellId(value as TSelectOption);
	};

	useEffect(() => {
		setSelectedCellId(options[0]);
	}, [options]);

	useEffect(() => {
		if (cellsData && 'items' in cellsData) {
			const data = cellsData.items?.find((item: Record<string, unknown>) => {
				return item.cellId === selectedCellId?.value;
			});

			setSelectedCellData(data || {});
		}
	}, [cellsData, selectedCellId]);

	const cellDataPrepared = useMemo(() => prepareCellData(selectedCellData), [selectedCellData]);

	const isGnodeBId = technologyValue.includes('5G');

	const gNodeBIdValue = site?.nokiaSite
		? isGnodeBId && site.nokiaSite.technology5g?.gnodeBId
		: isGnodeBId && selectedCellData.gnodeBId;

	const eNodeBIdValue = site?.nokiaSite
		? technologyValue === ETechnologiesValues.T_4G_TDD
			? site.nokiaSite.technology4gTdd?.enodeBId
			: technologyValue === ETechnologiesValues.T_4G_FDD && site.nokiaSite.technology4gFdd?.enodeBId
		: selectedCellData.enodeBId;

	const isNetworkParametersDisabled = (
		!site?.crowdCell?.id ||
		isInitialLoadingCrowdCellConfigDateExists ||
		!crowdCellConfigDateExists?.length
	);

	return cellsData ? (
		<>
			<div className={styles.infoContainer}>
				<Select
					className={styles.cellIdSelect}
					handleChange={handleChangeCellId}
					isWithoutUseForms
					label='Cell ID:'
					name='cellId'
					options={options}
					value={selectedCellId}
					width='120px'
				/>
				{selectedCellData && (
					<div>
						{isGnodeBId
							? <ParameterItem
								dataTestIdForValue='gnodeb-id'
								label='gNodeB ID'
								value={gNodeBIdValue}
							/>
							: <ParameterItem
								dataTestIdForValue='enodeb-id'
								label='eNodeB ID'
								value={eNodeBIdValue}
							/>
						}
						<ParameterItem
							dataTestIdForValue='cells-count'
							label='Cells'
							value={cellsData.count}
						/>
					</div>
				)}
			</div>
			<hr />
			<div className={styles.cellParams}>
				{Object.entries(cellDataPrepared).map(([key, value], idx) => (
					<ParameterItem
						key={`${key}-${idx}`}
						label={key}
						value={value}
					/>
				))}
				<Button
					className={styles.networkParametersButton}
					disabled={isNetworkParametersDisabled}
					isLoading={isInitialLoadingCrowdCellConfigDateExists}
					onClick={() => setIsShownConfiguration(true)}
					text={NETWORK_PARAMETERS_LABEL}
				/>
			</div>
			<Modal
				height='80%'
				isOpen={isShownConfiguration}
				onClose={closeConfigurationModal}
				title={NETWORK_PARAMETERS_LABEL}
				width={MODAL_WIDTH_580}
			>
				<NodeConfiguration
					closeModal={closeConfigurationModal}
					crowdCellId={site.crowdCell?.id}
					date={null}
					technology={technologyForConfig}
				/>
			</Modal>
		</>
	) : null;
};

export default memo(SiteParameters);
